import React, { useState } from 'react'
import styled from 'styled-components'
import Modal, { ModalHeader, ModalFooter } from '@app/components/Modal'
import * as S from '@app/components/Modal/styled'
import { fieldInputStyles } from '@app/components/molecules/FormFields/styled'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import getEnv from '@app/environments/getEnv'
import { useSnackbar } from '@app/snackbar'
import { SavedSlopeTable } from '../manualSlopeTableService'
import { useAxios } from '@common/hooks/useAxios'
import theme from '@app/ui/theme'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'

const EmailInput = styled.input<{ hasError?: boolean }>`
  ${fieldInputStyles}
  width: 100%;
  border-color: ${(props) =>
    props.hasError ? theme.colors.warning : theme.colors.dark1};
`

type DownloadOption = 'local' | 'email' | null

type Props = {
  isVisible: boolean
  table: SavedSlopeTable | null
  onClose: () => void
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const DownloadManualSlopeTableModal: React.FC<Props> = ({
  isVisible,
  table,
  onClose,
}) => {
  const [selectedOption, setSelectedOption] = useState<DownloadOption>(null)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [isSending, setIsSending] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [openSnackbar] = useSnackbar()
  const axios = useAxios('/ratings')

  const handleLocalDownload = async () => {
    if (!table) return
    setIsDownloading(true)
    try {
      const response = await fetch(
        `${getEnv().api}/ratings/forms/slopetable/pdf`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tees: JSON.stringify(table.tees),
            par: String(table.par),
            courseName: table.courseName,
            clubName: table.clubName,
            isDraft: String(table.isDraft),
            gender: table.gender,
            ratingDate: table.ratingDate,
            ratingName: table.ratingName,
            startHole: table.startHole,
            endHole: table.endHole,
          }),
        }
      )
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const date = table.ratingDate
        ? new Date(table.ratingDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]
      a.download = `SlopeTables_${table.clubName || 'Club'}-${
        table.courseName || 'Course'
      }-${date}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      openSnackbar('Failed to download slope table')
      console.error(err)
    } finally {
      setIsDownloading(false)
    }
    handleClose()
  }

  const handleEmailSend = async () => {
    if (!table) return

    if (!email.trim()) {
      setEmailError('Email is required')
      return
    }

    if (!isValidEmail(email.trim())) {
      setEmailError('Please enter a valid email address')
      return
    }

    setEmailError(null)
    setIsSending(true)
    try {
      await axios.post('/forms/email-slopetable', {
        email: email.trim(),
        clubName: table.clubName,
        courseName: table.courseName,
        ratingName: table.ratingName,
        tees: table.tees,
        par: table.par,
        isDraft: table.isDraft,
        gender: table.gender,
        ratingDate: table.ratingDate,
      })
      openSnackbar('Download link sent successfully')
      handleClose()
    } catch (err) {
      openSnackbar('Failed to send email link')
      console.error(err)
    } finally {
      setIsSending(false)
    }
  }

  const isLoading = isDownloading || isSending

  const handleClose = () => {
    if (isLoading) return
    setSelectedOption(null)
    setEmail('')
    setEmailError(null)
    onClose()
  }

  if (!table) return null

  return (
    <Modal
      isOpen={isVisible}
      onClose={handleClose}
      width="500px"
      height="auto"
      closeOnBackdropClick={!isLoading}
      showCloseButton={!isLoading}
    >
      <ModalHeader>
        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
          Download Slope Table
        </h2>
      </ModalHeader>
      <S.ModalScrollContent style={{ padding: '16px' }}>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Choose how you would like to download the slope table for{' '}
          <strong>{table.courseName || 'this course'}</strong>.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              border: `2px solid ${
                selectedOption === 'local' ? theme.colors.primary : '#e4e4e4'
              }`,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor:
                selectedOption === 'local' ? '#f0f7ff' : 'transparent',
            }}
            onClick={() => setSelectedOption('local')}
          >
            <input
              type="radio"
              name="downloadOption"
              value="local"
              checked={selectedOption === 'local'}
              onChange={() => setSelectedOption('local')}
              style={{ marginRight: '12px', cursor: 'pointer' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                Download PDF file locally
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Download the slope table directly to your device
              </div>
            </div>
            <FeatherIcon
              name="download"
              size={24}
              color={selectedOption === 'local' ? theme.colors.primary : '#999'}
              style={{ marginRight: '12px' }}
            />
          </label>

          <label
            style={{
              display: 'none',
              alignItems: 'center',
              padding: '12px',
              border: `2px solid ${
                selectedOption === 'email' ? theme.colors.primary : '#e4e4e4'
              }`,
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor:
                selectedOption === 'email' ? '#f0f7ff' : 'transparent',
            }}
            onClick={() => setSelectedOption('email')}
          >
            <input
              type="radio"
              name="downloadOption"
              value="email"
              checked={selectedOption === 'email'}
              onChange={() => setSelectedOption('email')}
              style={{ marginRight: '12px', cursor: 'pointer' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                Send email downloadable link
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Receive a download link via email
              </div>
            </div>
            <FeatherIcon
              name="mail"
              size={24}
              color={selectedOption === 'email' ? theme.colors.primary : '#999'}
              style={{ marginRight: '12px' }}
            />
          </label>
        </div>

        {selectedOption === 'email' && (
          <div style={{ marginTop: '20px' }}>
            <label
              htmlFor="email-input"
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              Email address
            </label>
            <EmailInput
              id="email-input"
              type="email"
              value={email}
              hasError={!!emailError}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) setEmailError(null)
              }}
              placeholder="recipient@example.com"
              disabled={isSending}
            />
            {emailError && (
              <div
                style={{
                  marginTop: '6px',
                  color: '#d32f2f',
                  fontSize: '12px',
                }}
              >
                {emailError}
              </div>
            )}
          </div>
        )}
      </S.ModalScrollContent>
      <ModalFooter>
        <RoundedButton
          title="Cancel"
          onPress={handleClose}
          disabled={isLoading}
          style={{
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.secondary}`,
            color: theme.colors.secondary,
          }}
          textStyle={{
            color: theme.colors.secondary,
          }}
        />
        {selectedOption === 'local' && (
          <RoundedButton
            title="Download"
            onPress={handleLocalDownload}
            disabled={isDownloading}
            loading={isDownloading}
            style={{
              backgroundColor: theme.colors.secondary2,
            }}
          />
        )}
        {selectedOption === 'email' && (
          <RoundedButton
            title={isSending ? 'Sending...' : 'Send Link'}
            onPress={handleEmailSend}
            disabled={isSending || !email.trim()}
            style={{
              backgroundColor: theme.colors.secondary2,
            }}
          />
        )}
      </ModalFooter>
    </Modal>
  )
}

export default DownloadManualSlopeTableModal
